import { createFileRoute } from "@tanstack/react-router";
import { useSimpleForm } from "@/hooks/use-simple-form";
import useAppStore from "@/store";
import { useTranslate } from "@/hooks/use-translate";
import SelectOrder from "@/components/order/select-order";
import { getNetAmount, type Order } from "@/store/Order";
import { useEffect, useState } from "react";
import SimpleTable from "@/components/ui/table";
import { SaleReturnCandidateTableColumns } from "@/components/sale-return/sale-return-meta";
import { generateRandomString } from "@/lib/utils";
import { InfoElement } from "@/components/ui/shared/details";
import { CreateSaleReturnTest } from "@/components/sale-return/sale-return-test";
import {
  getTotalRefundableAmount,
  getRefundAmount,
  type SaleReturnCandidate,
} from "@/store/SaleReturn";
import { getFullName } from "@/store/User";
import { Button } from "@/components/ui/button";
import BackButton from "@/components/ui/shared/back-button";

export const Route = createFileRoute("/(dasboard)/orders/returns/create")({
  component: CreateSaleReturn,
});

function CreateSaleReturn() {
  const store = useAppStore();
  const { t, formatDate } = useTranslate();
  const [order, setOrder] = useState<Order | undefined>();
  const [candidates, setCandidates] = useState<SaleReturnCandidate[]>([]);

  const form = useSimpleForm({
    order_id: "",
    reason: null,
  });

  useEffect(() => {
    if (store.auth.session) {
      store.financeAccount.fetch();
      store.order.fetch({});
    }
  }, [store.auth.session]);

  const submit = async () => {
    store.errors.reset();
    store.loading.start("process_return");

    store.saleReturn
      .createMany(
        candidates.map((item) => ({
          order_id: order?.id,
          sale_id: item.sale.id,
          quantity: item.quantity,
          amount: item.amount,
        }))
      )
      .then(() => {
        store.saleReturn.fetch();
        window.history.back();
      })
      .catch(store.errors.catch)
      .finally(() => {
        store.loading.stop("process_return");
      });
  };

  return (
    <section className="">
      {store.testing.canTest && (
        <CreateSaleReturnTest
          form={form}
          setOrder={setOrder}
          setCandidates={setCandidates}
        />
      )}
      <>{store.errors.render()}</>

      <BackButton />

      <header className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">{t("Process a return")}</h2>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <aside className="bg-white p-4 border-gray-200 border rounded-md">
          <div className="">
            <SelectOrder
              label="Order"
              order={order}
              onSelected={(order: Order | undefined) => {
                setOrder(order);
                if (order) {
                  setCandidates(
                    order?.sales.map((sale) => {
                      return {
                        id: generateRandomString(),
                        sale,
                        quantity: 0,
                        amount: 0,
                      };
                    }) as SaleReturnCandidate[]
                  );
                } else {
                  setCandidates([]);
                }
              }}
            />
            {order != undefined && (
              <ul className="text-sm mt-4 px-2 rounded-md space-y-2">
                <InfoElement
                  detail={{
                    label: t("Customer"),
                    value: order.customer_name,
                  }}
                />
                {[
                  {
                    label: t("Net Amount"),
                    value: store.shop.formatAmount(getNetAmount(order)),
                  },
                  {
                    label: t("Discount"),
                    value: store.shop.formatAmount(order.discount),
                  },
                  {
                    label: t("Delivery Fee"),
                    value: store.shop.formatAmount(order.delivery_fee),
                  },
                  { label: t("Serve by"), value: getFullName(order.user) },
                  {
                    label: t("Created At"),
                    value: formatDate(order.created_at, "dd MMM yyyy, HH:mm"),
                  },
                  {
                    label: t("Updated At"),
                    value: formatDate(order.updated_at, "dd MMM yyyy, HH:mm"),
                  },
                ].map((detail, index: number) => (
                  <InfoElement key={`order-details${index}`} detail={detail} />
                ))}
              </ul>
            )}
          </div>
        </aside>
        {candidates.length > 0 && (
          <aside className="col-span-2 bg-white p-6">
            <SimpleTable
              items={candidates}
              rowClass="hover:bg-white"
              containerClass="shadow-none border border-gray-200"
              columns={SaleReturnCandidateTableColumns({
                updateItem: (updates: Partial<SaleReturnCandidate>) => {
                  setCandidates((items: SaleReturnCandidate[]) => {
                    return items.map((item) => {
                      if (item.id == updates.id) {
                        return {
                          ...item,
                          ...updates,
                        };
                      } else {
                        return item;
                      }
                    });
                  });
                },
              })}
            />
            <div className="bg-gray-100 mt-2 rounded-md p-4 flex justify-end">
              <div className="w-1/2 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-700">
                    {t("Total Refundable Amount")}
                  </span>
                  <span className="font-semibold">
                    {store.shop.formatAmount(
                      getTotalRefundableAmount(candidates)
                    )}
                  </span>
                </div>
                <div className="border-t border-gray-200 my-2"></div>
                <div className="flex justify-between text-red-700">
                  <span className="">{t("Net Amount to refund")}</span>
                  <span className="font-semibold">
                    {store.shop.formatAmount(getRefundAmount(candidates))}
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-4">
              <Button className="px-6" onClick={submit}>
                {t("Submit")}
              </Button>
            </div>
          </aside>
        )}
      </section>
    </section>
  );
}
